import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, IconButton, Tooltip, TextField, MenuItem, Select,
  FormControl, InputLabel, Button, Divider, Checkbox,
  TablePagination, InputAdornment
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FiberManualRecord as DotIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Description as DocIcon,
  Visibility as PreviewIcon
} from '@mui/icons-material';
import hrDashboardService from '../../services/hrDashboardService';
import apiService from '../../services/apiService';

// Type labels
const TYPE_LABELS = {
  ProbationEnd: 'Probation End',
  MissingDoc: 'Missing Document',
  DocExpiring: 'Document Expiring',
  ContractExp: 'Contract Expiring',
  FollowUp: 'Follow-up',
  Task: 'Task'
};

// Traffic light colors for urgency
const URGENCY_COLORS = {
  Critical: '#e53935',
  High: '#fb8c00',
  Medium: '#fdd835',
  Low: '#66bb6a'
};

const FOLLOWUP_STATUSES = ['Pending', 'InProgress', 'Completed', 'Cancelled', 'Overdue'];

const HRDashboard = () => {
  // Data state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ kpi: null, items: [] });
  const [requiredDocs, setRequiredDocs] = useState([]);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Selection state
  const [selectedItem, setSelectedItem] = useState(null);

  // Preview pane state
  const [previewLoading, setPreviewLoading] = useState(false);
  const [documentChecklist, setDocumentChecklist] = useState([]);
  const [followUpForm, setFollowUpForm] = useState({ description: '', status: 'Pending', notes: '' });
  const [saving, setSaving] = useState(false);

  // Load action queue data
  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [queueResponse, docsResponse] = await Promise.all([
        hrDashboardService.getActionQueue(),
        apiService.get('/documents/requirements')
      ]);
      setData(queueResponse);
      setRequiredDocs((docsResponse || []).filter(d => d.required));
    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  // Load preview data when selection changes
  useEffect(() => {
    if (!selectedItem) {
      setDocumentChecklist([]);
      setFollowUpForm({ description: '', status: 'Pending', notes: '' });
      return;
    }

    const loadPreviewData = async () => {
      setPreviewLoading(true);
      try {
        if (selectedItem.type === 'MissingDoc' || selectedItem.type === 'DocExpiring') {
          // Load document checklist for this person
          if (!selectedItem.personId) {
            setDocumentChecklist([]);
            return;
          }
          const checklist = await apiService.get(`/dashboard/hr/person/${selectedItem.personId}/document-checklist`);
          setDocumentChecklist(checklist || []);
        } else if (selectedItem.type === 'FollowUp') {
          // Load follow-up details - use sourceId when sourceType is FollowUp
          if (selectedItem.sourceId && selectedItem.sourceType === 'FollowUp') {
            const followUp = await apiService.get(`/CareerEvents/followup/${selectedItem.sourceId}`);
            // Use status from API response, fallback to selectedItem.status, then 'Pending'
            const statusValue = followUp?.status || selectedItem.status || 'Pending';
            console.log('FollowUp loaded:', { followUp, statusValue, selectedItemStatus: selectedItem.status });
            setFollowUpForm({
              description: followUp?.description || '',
              status: statusValue,
              notes: followUp?.notes || ''
            });
          }
        } else if (selectedItem.type === 'Task') {
          // Task type - just show the details from the item itself
          setFollowUpForm({
            description: selectedItem.details || '',
            status: 'Pending',
            notes: ''
          });
        }
      } catch (err) {
        console.error('Failed to load preview data:', err);
      } finally {
        setPreviewLoading(false);
      }
    };

    loadPreviewData();
  }, [selectedItem]);

  // Filter and search logic
  const filteredItems = useMemo(() => {
    let items = data.items || [];

    // Type filter
    if (typeFilter !== 'all') {
      if (typeFilter === 'Tasks') {
        items = items.filter(i => i.type === 'Task' || i.type === 'FollowUp');
      } else {
        items = items.filter(i => i.type === typeFilter);
      }
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(i =>
        (i.employeeName || '').toLowerCase().includes(q) ||
        (i.details || '').toLowerCase().includes(q) ||
        (TYPE_LABELS[i.type] || i.type || '').toLowerCase().includes(q)
      );
    }

    return items;
  }, [data.items, typeFilter, searchQuery]);

  // Paginated items
  const paginatedItems = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredItems.slice(start, start + rowsPerPage);
  }, [filteredItems, page, rowsPerPage]);

  // Handlers
  const handleRowClick = (item) => {
    // Use sourceId + type as unique identifier (sourceId alone is not unique across types)
    const isSameItem = selectedItem?.sourceId === item.sourceId && selectedItem?.type === item.type;
    setSelectedItem(isSameItem ? null : item);
  };

  const handleSaveFollowUp = async () => {
    if (!selectedItem?.sourceId || selectedItem?.sourceType !== 'FollowUp') return;
    setSaving(true);
    try {
      await apiService.put(`/CareerEvents/followup/${selectedItem.sourceId}/status`, {
        status: followUpForm.status,
        notes: followUpForm.notes
      });
      await loadData();
      setSelectedItem(null);
    } catch (err) {
      console.error('Failed to save follow-up:', err);
    } finally {
      setSaving(false);
    }
  };

  const formatUrgency = (item) => {
    if (item.daysRemaining === null || item.daysRemaining === undefined) return '—';
    if (item.daysRemaining < 0) return `${Math.abs(item.daysRemaining)}d overdue`;
    if (item.daysRemaining === 0) return 'Today';
    return `in ${item.daysRemaining}d`;
  };

  const getUrgencyColor = (urgency) => URGENCY_COLORS[urgency] || URGENCY_COLORS.Low;

  // Count items by type
  const typeOptions = useMemo(() => {
    const counts = {};
    (data.items || []).forEach(i => {
      const t = i.type === 'Task' ? 'Tasks' : i.type === 'FollowUp' ? 'Tasks' : i.type;
      counts[t] = (counts[t] || 0) + 1;
    });
    return [
      { value: 'all', label: `All (${data.items?.length || 0})` },
      ...Object.entries(counts).map(([k, v]) => ({
        value: k,
        label: `${TYPE_LABELS[k] || k} (${v})`
      }))
    ];
  }, [data.items]);

  // Render Preview Pane content
  const renderPreviewContent = () => {
    if (previewLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    // No selection - show default required docs list
    if (!selectedItem) {
      return (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Required Documents
          </Typography>
          {requiredDocs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No required documents configured</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {requiredDocs.map((doc) => (
                <Box key={doc.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  <DocIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Box>
                    <Typography variant="body2">{doc.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{doc.phase}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      );
    }

    // Document task - show checklist
    if (selectedItem.type === 'MissingDoc' || selectedItem.type === 'DocExpiring') {
      const presentCount = documentChecklist.filter(d => d.status === 'Present').length;
      return (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {selectedItem.employeeName}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            {presentCount}/{documentChecklist.length} documents present
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {documentChecklist.map((doc) => (
            <Box
              key={doc.requirementId || doc.id}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1, py: 0.75,
                borderBottom: '1px solid', borderColor: 'divider'
              }}
            >
              {doc.status === 'Present' ? (
                <CheckIcon sx={{ fontSize: 18, color: 'success.main' }} />
              ) : (
                <CancelIcon sx={{ fontSize: 18, color: 'error.main' }} />
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2">{doc.name}</Typography>
                {doc.expiryDate && (
                  <Typography variant="caption" color="text.secondary">
                    Expires: {new Date(doc.expiryDate).toLocaleDateString()}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      );
    }

    // Follow-up task - show form
    if (selectedItem.type === 'FollowUp' || selectedItem.type === 'Task') {
      return (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            {selectedItem.employeeName}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
            {selectedItem.details}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <TextField
            label="Description"
            value={followUpForm.description}
            onChange={(e) => setFollowUpForm({ ...followUpForm, description: e.target.value })}
            fullWidth
            multiline
            rows={3}
            size="small"
            sx={{ mb: 2 }}
            disabled
          />

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={followUpForm.status}
              label="Status"
              onChange={(e) => setFollowUpForm({ ...followUpForm, status: e.target.value })}
            >
              {FOLLOWUP_STATUSES.map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Notes"
            value={followUpForm.notes}
            onChange={(e) => setFollowUpForm({ ...followUpForm, notes: e.target.value })}
            fullWidth
            multiline
            rows={2}
            size="small"
            sx={{ mb: 2 }}
          />

          <Button
            variant="contained"
            onClick={handleSaveFollowUp}
            disabled={saving}
            fullWidth
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      );
    }

    // Other types - show basic info
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {selectedItem.employeeName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {TYPE_LABELS[selectedItem.type] || selectedItem.type}
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          {selectedItem.details}
        </Typography>
        {selectedItem.subDetails && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {selectedItem.subDetails}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 120px)', gap: 2, p: 2 }}>
      {/* Left Panel - Action Queue */}
      <Paper sx={{ flex: '0 0 65%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Toolbar */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Search */}
            <TextField
              placeholder="Search by keyword"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
              size="small"
              sx={{ minWidth: 200, flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
            />

            {/* Type Filter */}
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                label="Type"
                onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
              >
                {typeOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Refresh */}
            <Tooltip title="Refresh">
              <IconButton onClick={loadData} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Items count */}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {filteredItems.length} items found
          </Typography>
        </Box>

        {/* Error */}
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        {/* Table */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ flex: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ bgcolor: 'background.paper' }}></TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Details</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }} align="right">Due</TableCell>
                    <TableCell sx={{ fontWeight: 600, bgcolor: 'background.paper' }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedItems.map((item, idx) => {
                    const isSelected = selectedItem?.sourceId === item.sourceId && selectedItem?.type === item.type;
                    return (
                      <TableRow
                        key={`${item.type}-${item.sourceId}-${idx}`}
                        hover
                        selected={isSelected}
                      >
                        <TableCell padding="checkbox">
                          <DotIcon sx={{ color: getUrgencyColor(item.urgency), fontSize: 12 }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontSize={13}>
                            {TYPE_LABELS[item.type] || item.type}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500} fontSize={13}>
                            {item.employeeName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontSize={13}>{item.details}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontSize={13} sx={{ color: item.status === 'Completed' ? 'success.main' : item.status === 'Cancelled' ? 'text.disabled' : 'text.primary' }}>
                            {item.status || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontSize={13}
                            sx={{ color: getUrgencyColor(item.urgency), fontWeight: item.urgency === 'Critical' ? 600 : 400 }}
                          >
                            {formatUrgency(item)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Preview">
                            <IconButton size="small" onClick={() => handleRowClick(item)} color={isSelected ? 'primary' : 'default'}>
                              <PreviewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {paginatedItems.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No items found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={filteredItems.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        )}
      </Paper>

      {/* Right Panel - Preview Pane */}
      <Paper sx={{ flex: '0 0 35%', p: 2, overflow: 'auto' }}>
        <Typography variant="overline" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Preview
        </Typography>
        {renderPreviewContent()}
      </Paper>
    </Box>
  );
};

export default HRDashboard;
